import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

function renderBlock(block) {
  const { type, id } = block;
  const value = block[type];
  const text = value?.rich_text?.map(t => t.plain_text).join('') ?? '';

  switch (type) {
    case 'paragraph':
      return text ? <p key={id} className="text-on-surface-variant leading-relaxed mb-5" style={{ fontSize: '1.1rem' }}>{text}</p> : <br key={id} />;
    case 'heading_1':
      return <h2 key={id} className="font-bold text-primary mt-10 mb-4" style={{ fontSize: 'clamp(1.6rem, 2.5vw, 2.25rem)' }}>{text}</h2>;
    case 'heading_2':
      return <h3 key={id} className="font-bold text-primary mt-8 mb-3" style={{ fontSize: 'clamp(1.3rem, 2vw, 1.75rem)' }}>{text}</h3>;
    case 'heading_3':
      return <h4 key={id} className="font-semibold text-primary mt-6 mb-2" style={{ fontSize: 'clamp(1.1rem, 1.5vw, 1.4rem)' }}>{text}</h4>;
    case 'bulleted_list_item':
      return <li key={id} className="text-on-surface-variant mb-2 ml-6 list-disc" style={{ fontSize: '1.1rem' }}>{text}</li>;
    case 'numbered_list_item':
      return <li key={id} className="text-on-surface-variant mb-2 ml-6 list-decimal" style={{ fontSize: '1.1rem' }}>{text}</li>;
    case 'image': {
      const src = value?.file?.url ?? value?.external?.url ?? '';
      const caption = value?.caption?.[0]?.plain_text ?? '';
      return <img key={id} src={src} alt={caption} className="rounded-xl my-8 w-full object-cover" />;
    }
    case 'divider':
      return <hr key={id} className="my-8 border-outline-variant/30" />;
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`/api/notion-post?slug=${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(data => { setPost(data); document.title = `${data.title} | LumaSeeker`; setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [slug]);

  return (
    <div className="min-h-screen bg-surface text-on-surface" style={{ fontFamily: "'Lexend', sans-serif" }}>

      {/* Nav */}
      <header className="w-full sticky top-0 z-50 bg-surface border-b border-outline-variant/20">
        <nav className="flex justify-between items-center px-6 md:px-12 py-5 max-w-screen-2xl mx-auto">
          <Link to="/" className="flex items-center gap-3">
            <img src="/favicon-32x32.png" alt="LumaSeeker" className="h-10 w-auto" />
            <span className="text-xl font-bold tracking-wider text-primary uppercase">LumaSeeker</span>
          </Link>
          <Link to="/blog" className="btn-primary px-5 py-2 font-bold text-sm">← All Articles</Link>
        </nav>
      </header>

      <main className="px-6 md:px-12 py-16 max-w-3xl mx-auto">
        {loading && <p className="text-on-surface-variant text-lg">Loading…</p>}
        {notFound && <p className="text-on-surface-variant text-lg">Article not found.</p>}
        {post && (
          <>
            <p className="text-secondary text-sm font-semibold uppercase tracking-wide mb-4">{post.date}</p>
            <h1 className="font-extrabold text-primary mb-6" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', lineHeight: 1.1 }}>
              {post.title}
            </h1>
            {post.description && (
              <p className="text-on-surface-variant text-xl leading-relaxed mb-10 border-l-4 border-secondary pl-6">
                {post.description}
              </p>
            )}
            <div>{post.blocks.map(renderBlock)}</div>

            {/* CTA */}
            <div className="mt-16 rounded-2xl p-10 text-center" style={{ background: '#002f1d' }}>
              <h2 className="font-bold mb-3" style={{ color: '#baeed1', fontSize: '1.75rem' }}>Try the LumaSeeker P2</h2>
              <p className="mb-6" style={{ color: 'rgba(186,238,209,0.75)' }}>Hands-free 5X magnification with a 90-day comfort guarantee.</p>
              <Link to="/#cta">
                <button className="rounded-lg font-bold uppercase" style={{ background: '#795900', color: '#fff', fontSize: '1rem', padding: '0.9rem 2rem' }}>
                  Order Today
                </button>
              </Link>
            </div>
          </>
        )}
      </main>

      <footer className="border-t border-outline-variant/20 px-6 md:px-12 py-8 text-center text-on-surface-variant text-sm">
        © 2026 LumaSeeker. Empowering vision for a brighter tomorrow.
      </footer>
    </div>
  );
}
